// Misc
import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import omitBy from 'lodash/omitBy'
import kebabCase from 'lodash/kebabCase'
import compact from 'lodash/compact'
import pickBy from 'lodash/pickBy'
import first from 'lodash/first'
import keys from 'lodash/keys'
import jwtDecode from 'jwt-decode'

// Validation
import { Validator } from 'react-vld'

// Actions
import connectActions from 'utils/connectActions'
import { signUp } from 'actions/me'
import { fetchTimezone } from 'actions/timezone'
import { logOnboarding } from 'actions/onboarding'
import { createPaymentSource } from 'actions/billing'

// Selectors
import { connectGetters } from 'react-redux-getters'
import { getMe } from 'selectors/me'

// Components
import { AppProgressActivator } from 'atoms/AppProgress'
import { ScrollContainer } from 'atoms/ScrollSections'
import WidgetPreview from 'components/Widget/Preview'
import AccountStep from './AccountStep'
import UserDetailsStep from './UserDetailsStep'
import BillingStep from './BillingStep'
import WelcomeMessageStep from './WelcomeMessageStep'
import CustomizationStep from './CustomizationStep'
import FinishStep from './FinishStep'
import Logo from './Logo'

const STORAGE_PLAN_KEY = 'onboarding-plan'
const STORAGE_COUPON_KEY = 'onboarding-coupon'

const save = (key, value) => {
  window.localStorage.setItem(key, value)
}

const getSaved = (key) => {
  return window.localStorage.getItem(key)
}

const clearSaved = (key) => {
  window.localStorage.removeItem(key)
}

const mapActionsToProps = {
  signUpAction: signUp,
  fetchTimezoneAction: fetchTimezone,
  logOnboardingAction: logOnboarding,
  createPaymentSourceAction: createPaymentSource,
}

const mapGettersToProps = state => ({
  meGetter: getMe(state)
})

export default
@connectActions(mapActionsToProps)
@connectGetters(mapGettersToProps)
class Onboarding extends Component {
  static propTypes = {
    jwtData: PropTypes.any,
    meGetter: PropTypes.object.isRequired,
    signUpAction: PropTypes.object.isRequired,
    fetchTimezoneAction: PropTypes.object.isRequired,
    logOnboardingAction: PropTypes.object.isRequired,
    createPaymentSourceAction: PropTypes.object.isRequired,
  }

  constructor({ jwtData, plan: planProp, coupon }) {
    super()

    let externalAcc
    try {
      externalAcc = jwtDecode(jwtData)
    } catch (e) {
      // do nothing
    }

    const plan = planProp || getSaved(STORAGE_PLAN_KEY) || 'free'
    if (plan) {
      save(STORAGE_PLAN_KEY, plan)
    }
    if (coupon) {
      save(STORAGE_COUPON_KEY, coupon)
    }

    const firstName = (externalAcc && externalAcc.firstName) || ''
    const lastName = (externalAcc && externalAcc.firstName ? (externalAcc.lastName || '') : '') || ''
    const businessName = compact([firstName, lastName]).join(' ')
      || (externalAcc && externalAcc.externalUserName) || ''

    this.state = {
      externalAcc,
      card: null,
      pending: false,
      submitError: null,
      random: Math.random().toString(36).slice(2),
      formData: {
        plan,
        coupon: '',
        acceptedAgreementVersion: null,
        email: (externalAcc && externalAcc.email) || '',
        password: '',
        firstName,
        lastName,
        image: (externalAcc && externalAcc.image) || '',
        businessName,
        businessUrl: '',
        businessCategory: '',
        businessDomain: (externalAcc && kebabCase(externalAcc.externalUserName)) || '',
        externalAccount: jwtData,
      },
    }
  }

  fetchTimezone = async () => {
    const { fetchTimezoneAction } = this.props
    try {
      const timezone = await fetchTimezoneAction.run()
      this.logOnboardingData('timezone', timezone)
      return timezone
    } catch (e) {
      return null
    }
  }

  createCardToken = async () => {
    const { card } = this.state
    const { error, token } = await card.createToken()
    if (error) throw error
    return token
  }

  createPaymentSource = async (token) => {
    const { createPaymentSourceAction } = this.props
    try {
      await createPaymentSourceAction.run({
        params: [token],
      })
    } catch (e) {
      // do nothing
    }
  }

  signUp = async (timezone) => {
    const omitEmptyFields = data => omitBy(data, field => !field)
    const { signUpAction } = this.props
    const { formData } = this.state
    const {
      businessUrl,
      businessName,
      businessDomain,
      businessCategory,
      firstName,
      lastName,
      ...rest
    } = formData

    const userData = {
      ...omitEmptyFields(rest),
      firstName,
      lastName,
      business: omitEmptyFields({
        name: businessName || `${firstName} ${lastName}`,
        url: businessUrl,
        domain: businessDomain,
        category: businessCategory,
        currency: 'USD',
        timezone,
      }),
    }

    await signUpAction.run({
      params: [userData],
    })
  }

  handleComplete = validation => async (skipCard) => {
    const { pending, card } = this.state
    const { meGetter } = this.props
    const validatorState = validation.validate()

    if (validatorState.isInvalid) {
      const { sectionIndex } = validatorState.error.payload
      this.scrollContainer.activateSection(sectionIndex)
      return
    }

    if (pending) return
    if (!meGetter.isFailed) return
    if (card && !card.complete && !skipCard) return

    this.setState({ submitError: null, pending: true })

    try {
      this.logOnboardingData('signup-begin', true)
      const timezone = await this.fetchTimezone()

      // Sign up with card
      if (card && card.complete) {
        // Create token before sign up to catch error
        const token = await this.createCardToken()
        await this.signUp(timezone)
        await this.createPaymentSource(token)
      } else {
        await this.signUp(timezone)
      }

      this.logOnboardingData('signup-success', true)
      // Scroll to welcome message section
      this.scrollContainer.activateSection(3)
      clearSaved(STORAGE_PLAN_KEY)
      clearSaved(STORAGE_COUPON_KEY)
      this.setState({ pending: false })
    } catch (error) {
      // do nothing
      this.logOnboardingData('signup-error', JSON.stringify(error))
      this.setState({ submitError: error, pending: false })
    }
  }

  handleCardChange = (card) => {
    this.setState({ card })
  }

  handleFormDataUpdate = (nextFormData, callback) => {
    const { formData } = this.state
    this.setState({
      formData: { ...formData, ...nextFormData }
    }, () => {
      if (callback) callback()
      // Find changed field
      const changedData = pickBy(nextFormData, (value, key) => value !== formData[key])
      const changedFieldName = first(keys(changedData))

      if (!changedFieldName) {
        // Something wrong
        return
      }

      // Make log data
      const value = changedFieldName === 'password' ? '******' : changedData[changedFieldName]
      this.logOnboardingData(changedFieldName, value)
    })
  }

  logOnboardingData = (field, value) => {
    const { logOnboardingAction } = this.props
    const { formData = {}, random } = this.state
    const { email, acceptedAgreementVersion } = formData
    const logData = {
      random,
      field,
      accepted: !!acceptedAgreementVersion,
      email,
      value,
    }
    // Run action
    logOnboardingAction.run({
      params: [logData],
      updateComponent: false,
      silent: true,
    })
  }

  scrollContainerRef = (component) => {
    this.scrollContainer = component
  }

  render() {
    const { meGetter } = this.props
    const { formData, externalAcc, submitError, pending } = this.state

    const stepProps = {
      formData,
      onFormDataUpdate: this.handleFormDataUpdate,
      disabled: pending || meGetter.isSucceded,
    }

    const {
      email, password, acceptedAgreementVersion,
      firstName, lastName, businessDomain
    } = formData

    const isLogged = meGetter.isSucceded
    const showUserDetailsStep = isLogged || (!!email && !!password && !!acceptedAgreementVersion)
    const showBillingStep = isLogged || (showUserDetailsStep && !!firstName && !!lastName && !!businessDomain) // eslint-disable-line
    const showWelcomeMessageStep = isLogged || showBillingStep
    const showCustomizationStep = isLogged || showWelcomeMessageStep
    const showFinishStep = isLogged || showCustomizationStep

    return (
      <Fragment>
        { pending && <AppProgressActivator /> }
        { isLogged && <WidgetPreview /> }
        <Logo />
        <Validator>
          { validation => (
            <ScrollContainer
              innerRef={this.scrollContainerRef}
              defaultActiveSectionIndex={isLogged ? 3 : 0}
            >
              <AccountStep {...stepProps} externalAcc={externalAcc} />
              { showUserDetailsStep && (
                <UserDetailsStep
                  {...stepProps}
                />
              ) }
              { showBillingStep && (
                <BillingStep
                  {...stepProps}
                  onComplete={this.handleComplete(validation)}
                  onCardChange={this.handleCardChange}
                  submitError={submitError}
                />
              ) }
              { showWelcomeMessageStep && (
                <WelcomeMessageStep
                  user={meGetter.data}
                  disabled={pending || !isLogged}
                />
              ) }
              { showCustomizationStep && (
                <CustomizationStep
                  meGetter={meGetter}
                  disabled={pending || !isLogged}
                />
              ) }
              { showFinishStep && (
                <FinishStep
                  meGetter={meGetter}
                  disabled={pending || !isLogged}
                />
              ) }
            </ScrollContainer>
          ) }
        </Validator>
      </Fragment>
    )
  }
}
